/* ---------------------------------------------------------------------------
   The nav pane.

   TWO LISTS, AND THE SEAM BETWEEN THEM IS THE WHOLE IDEA. At the top, the screens
   of the application you are in — never more than seven, usually four. At the
   foot, the other applications on this site, because moving between them is a
   page load and that is a different kind of act from moving between screens. See
   src/core/apps.ts.

   Which screens appear is `app.screens` and not a list kept here. That is what
   stops the Scheduler's pane offering the inventory sheet: a screen belongs to an
   app in one file, and this one draws whatever that file says. The numbers beside
   them are positions in THIS app's list, so `2` means something different in
   Bridge Inspection and in Construction Monitoring — which is right, because they
   are different applications, and a key that meant "the register" in one and
   nothing at all in the other would be worse than a key that means the second
   thing in the pane you are looking at.

   THE TWO ACTIONS UNDER THE SELECTED STRUCTURE BELONG TO THE REGISTER, and only
   to it. That is the one screen where selecting a row deliberately does not
   navigate — see the note on the row handler in src/screens/register.ts — so the
   pane is where opening the record and logging a visit have to be reachable from.
   Everywhere else they were a second copy of a control the screen already
   carries: the record's own page head offers both, and a button that says "Open
   record" on the record is an invitation to press it and arrive where you
   already are.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type IconPath } from '../core/dom.ts';
import { actions, can, canManagePeople, type State } from '../core/store.ts';
import { APPS, appForShell, type AppDef } from '../core/apps.ts';
import { appHref } from '../core/navigate.ts';
import { button } from './widgets.ts';
import { findBridge } from '../data/derive.ts';
import { t } from '../data/locale.ts';
import type { BridgeView, Screen } from '../data/types.ts';

export interface NavEntry {
  screen: Screen;
  label: string;
  iconPath: IconPath;
  /**
   * The key that goes here, shown at the end of the row.
   *
   * A position in the list the reader is actually looking at, assigned in
   * `visibleNav` after the filtering rather than written into a table. See the
   * note there: a key printed on a control has to be the key that works, and
   * nothing else may be bound.
   */
  key: string;
}

/**
 * What every screen in this build is called in a nav pane, and what it wears.
 *
 * ONE ENTRY PER SCREEN, NOT PER DESTINATION. A screen that several apps carry —
 * `list`, which is how a structure is picked in three of them — is named once
 * here and drawn in each of their panes. The alternative is the register called
 * one thing in the Scheduler and another in Monitoring, which is exactly the
 * drift this table exists to make impossible.
 *
 * EXPORTED SO THE GUIDE CANNOT DRIFT FROM IT. src/screens/guide.ts describes the
 * site app by app, and the check in `npm test` walks the two against each other.
 */
export const SCREEN_NAV: Readonly<Record<Screen, { label: string; iconPath: IconPath }>> = {
  dashboard: { label: 'Home', iconPath: ICONS.grid },
  /* The structure in hand, every application's answer at once (LC-30). */
  board: { label: 'Bridge dashboard', iconPath: ICONS.chart },
  list: { label: 'Register', iconPath: ICONS.list },
  detail: { label: '3D model', iconPath: ICONS.cube },
  network: { label: 'Network', iconPath: ICONS.map },
  report: { label: 'Inventory sheet', iconPath: ICONS.print },
  sheet: { label: 'Inspection summary sheet', iconPath: ICONS.report },
  summary: { label: 'Network summary', iconPath: ICONS.report },
  schedule: { label: 'Programme', iconPath: ICONS.gantt },
  site: { label: 'Site record', iconPath: ICONS.hardhat },
  works: { label: 'Period report', iconPath: ICONS.print },
  /* Deliberately not first in any app. A guide at the top of a workspace list is
     a guide that reads as the thing you are meant to start with; this one is
     where somebody looks when they are already here and stuck. */
  guide: { label: 'Guide', iconPath: ICONS.book },
  /* Administering the site rather than reading the register, and the entry most
     of this list's readers never see at all — see `visibleNav`. */
  people: { label: 'People', iconPath: ICONS.people },
  /* Field shell only. The desk pane never draws it, and the field shell has a
     tab bar rather than a pane — it is named here so that every Screen has a
     name, which is what makes this table exhaustive rather than nearly so. */
  options: { label: 'Options', iconPath: ICONS.settings },
};

/** A screen's name mid-sentence: "site record", but "3D model" keeps its capital. */
function inSentence(label: string): string {
  return /^.[A-Z]/.test(label) ? label : label.toLowerCase();
}

/**
 * Every destination an app has, in the order it draws them, unnumbered.
 *
 * Unnumbered because a number is a position in a list that has been filtered,
 * and this list has not been — see `visibleNav`. What this is for is describing
 * an application: the guide names these, and so does anything that has to say
 * what an app contains rather than what this reader may reach in it.
 */
export function navFor(app: AppDef): Omit<NavEntry, 'key'>[] {
  return app.screens.map((screen) => ({
    screen,
    label: SCREEN_NAV[screen].label,
    iconPath: SCREEN_NAV[screen].iconPath,
  }));
}

/**
 * The destinations THIS person gets, in the app they are in.
 *
 * Two rules, one entry each. People is withheld from anybody who cannot grant a
 * role — withheld and not disabled, because a greyed-out destination is an
 * invitation to ask why, repeatedly, and there is nothing on that screen an
 * inspector could act on if they arrived. The screen refuses as well, because a
 * pasted `#/people` link never went past this list. Options belongs to the field
 * shell, which has no pane, so the desk pane drops it.
 *
 * `navFor` stays whole: the guide describes every screen this site has,
 * including the one most readers cannot reach.
 */
export function visibleNav(s: Readonly<State>): NavEntry[] {
  return navFor(appForShell(s.shell))
    .filter(
      (e) =>
        (e.screen !== 'people' || canManagePeople(s)) &&
        (e.screen !== 'options' || s.shell === 'field'),
    )
    /* NUMBERED AFTER THE FILTERING, which is the only order that keeps the
       promise the pane makes. The digits are printed on these rows and bound
       from this same list (see src/main.ts), so every key shown works and no key
       reaches a row that is not there — an administrator's `3` and an
       inspector's `3` are different screens, and each is the third thing in the
       pane in front of them. Numbering before the filter would have left gaps
       where People is withheld, and bound `6` on a desk browser to the field
       shell's Options screen. */
    .map((e, i) => ({ ...e, key: String(i + 1) }));
}

/**
 * The rest of the site, at the foot of the pane.
 *
 * Anchors rather than buttons, because each one is another document and a real
 * href is what makes it behave like an address — middle-clickable, copyable,
 * and shown in the status bar before it is pressed. See the header of
 * src/components/appLinks.ts. The structure in hand travels with it wherever the
 * target has somewhere to put one, so leaving a record for the programme opens
 * that bridge's programme.
 */
function appRail(s: Readonly<State>, here: AppDef): HTMLElement {
  return h(
    'div',
    { class: 'navapps' },
    /* The inset is the label's own (`0 16px 8px`); only the top is overridden,
       because the group already carries a rule above it. Written as a full
       shorthand rather than `padding:0 0 6px`, which zeroed the left inset too
       and hung the A of APPLICATIONS off the edge of the pane. */
    s.navOpen
      ? h('div', { class: 'nav__label', style: 'padding:0 16px 6px', text: t('Applications') })
      : null,
    ...APPS.filter((a) => a.id !== here.id).map((a) =>
      h(
        'a',
        { class: 'navitem navitem--app', href: appHref(s, a.id), title: a.blurb },
        h('span', { class: 'navitem__icon' }, icon(a.iconPath, { size: 17 })),
        s.navOpen ? h('span', { class: 'navitem__text', text: a.label }) : null,
      ),
    ),
  );
}

/**
 * The structure in hand, and on the register the things most often done to it.
 *
 * `actionable` is true on the register and nowhere else. See the note at the top:
 * everywhere else the buttons duplicated a control already on the page.
 *
 * THE BUTTON IS NAMED AFTER WHERE IT GOES, and where it goes depends on the
 * application: the record in Bridge Inspection, the programme in the Scheduler,
 * the site record in Monitoring. One action does all three (`openBridge` reads
 * the app), and the label has to follow it — a pane in the Scheduler offering
 * "Open record" would be a button whose text named another application's screen.
 */
function selectedStructure(
  b: BridgeView,
  here: AppDef,
  actionable: boolean,
  mayLog: boolean,
): HTMLElement {
  const target = here.structure;

  return h(
    'div',
    { class: 'navsel' },
    h('div', { class: 'nav__label', style: 'padding:0 0 8px', text: t('Selected structure') }),
    h('div', { class: 'navsel__name', text: b.name }),
    h('div', { class: 'navsel__meta', text: `${b.id} · ${b.routeKm}` }),
    /* No icons on either: they are the only buttons in the pane, they are full
       width, and a glyph beside a centred label just pulls it off centre. */
    actionable && target
      ? button(t(`Open ${inSentence(SCREEN_NAV[target].label)}`), {
          primary: true,
          block: true,
          tall: true,
          title: `Open ${inSentence(SCREEN_NAV[target].label)} for ${b.name}`,
          onClick: () => actions.openBridge(b.id),
        })
      : null,
    /* Bridge Inspection's, and only its. Logging an inspection from the
       Scheduler's pane would be this application doing another one's work in a
       corner of the screen where nothing says which application it was.
       `inspection.log` as well: not drawn for a role that does not record
       inspections, which leaves the one control everybody may press on its own. */
    actionable && mayLog && here.id === 'inspection'
      ? h(
          'div',
          { style: 'margin-top:8px' },
          button(t('Log inspection'), {
            block: true,
            tall: true,
            title: `Add an inspection entry to ${b.name}`,
            onClick: () => actions.openAddInspection(b.id),
          }),
        )
      : null,
  );
}

/**
 * Which screens have no one structure in hand, so the pane draws none.
 *
 * Home reads the register as a whole; the guide and People are about the site
 * rather than about any structure. Everything else in every app is either a
 * structure screen or a list you pick one from.
 */
const NO_STRUCTURE: readonly Screen[] = ['dashboard', 'guide', 'people'];

/**
 * The start page's own addition to its list: New bridge, for whoever may write
 * the inventory record. A button rather than a screen — it opens the edit
 * dialog on a blank record — so it carries no number key.
 *
 * The Guide is not drawn here, though it is still a Home screen: the title bar's
 * help button is the way to it, and a second entry for it sat between Home and
 * the one thing this pane is for.
 */
function homeRows(here: AppDef, rows: NavEntry[]): NavEntry[] {
  return here.id === 'home' ? rows.filter((e) => e.screen !== 'guide' && e.screen !== 'people') : rows;
}

function newBridge(s: Readonly<State>, here: AppDef): HTMLElement | null {
  if (here.id !== 'home' || !(can(s, 'inventory.edit') || can(s, 'structure.create'))) return null;
  return h(
    'button',
    {
      type: 'button',
      class: 'navitem',
      title: 'Add a structure to the register',
      onclick: () => actions.openNewBridge(),
    },
    h('span', { class: 'navitem__icon' }, icon(ICONS.plus, { size: 17 })),
    s.navOpen ? h('span', { class: 'navitem__text', text: t('New bridge') }) : null,
  );
}

/** One destination row, with its number key. */
function navRow(s: Readonly<State>, entry: NavEntry, style?: string): HTMLElement {
  const on = entry.screen === s.screen;
  return h(
    'button',
    {
      type: 'button',
      class: ['navitem', on && 'navitem--on'],
      style,
      title: `${t(entry.label)} (${entry.key})`,
      'aria-current': on ? 'page' : undefined,
      onclick: () => actions.go(entry.screen),
    },
    h('span', { class: 'navitem__icon' }, icon(entry.iconPath, { size: 17 })),
    s.navOpen ? h('span', { class: 'navitem__text', text: t(entry.label) }) : null,
    s.navOpen ? h('span', { class: 'navitem__key', text: entry.key }) : null,
  );
}

export function navpane(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  const here = appForShell(s.shell);

  return h(
    'nav',
    {
      class: ['nav', s.navOpen ? 'nav--open' : 'nav--closed'],
      'aria-label': 'Main',
    },

    /* The app's own name over its screens. Without it the pane is a list of
       four things with no statement of what they are four things IN — which was
       survivable when there was one application and is not now that a reader can
       have two of them open in two tabs. */
    s.navOpen
      ? h('div', { class: 'nav__app' }, h('span', { class: 'nav__app-name', text: here.brand }))
      : null,

    h('div', { class: 'nav__label', text: t('Workspace') }),

    ...homeRows(here, visibleNav(s)).map((entry) => navRow(s, entry)),

    newBridge(s, here),

    /* The selected structure. Not on the screens that have no one structure in
       hand, and with its two actions only on the register, which is the one
       screen that cannot reach them otherwise.

       `b` is null only where the register is empty, and then there is no
       selected structure to show — the block is dropped rather than drawn with
       dashes in it. */
    b === null || NO_STRUCTURE.includes(s.screen)
      ? null
      : selectedStructure(b, here, s.screen === 'list', can(s, 'inspection.log')),

    /* THE OTHER APPLICATIONS SIT WITH THE REST OF THE NAVIGATION, not at the
       foot of the pane. They were pinned to the bottom at first, on the reading
       that leaving for another application is a different kind of act from
       moving between screens — which it is — but the home page has two
       destinations in its workspace list and that put a rule and four
       applications an inch above the bottom edge with a hand's width of nothing
       between. A list nobody's eye reaches is a list nobody presses.

       So: straight after the screens, and after the selected structure where
       there is one — the register's "Open record" is the primary action on the
       screen that carries it and must not be pushed below a list of other
       applications. The spacer is now below this, which is what keeps the
       provenance note and the collapse control on the bottom edge. */
    /* On Home too, now that its page is the register and the network rather
       than a grid of application cards: this list is the way into the four. */
    appRail(s, here),

    h('span', { class: 'spacer' }),

    /* The rail toggle sits last so it is the closest control to the bottom edge,
       where a collapse affordance is conventionally found. It renders in both
       states, unlike the labels — collapsing must not make anything
       unreachable. */
    /* Settings at the foot of the start page's pane, where a NavigationView
       keeps it: Options, which belongs to Bridge Inspection and says who you are
       signed in as. */
    /* People, at the foot beside Settings: administering who may use the
       register is not one of the places the work is done. `visibleNav` has
       already withheld it from anybody but an administrator. */
    ...(here.id === 'home'
      ? visibleNav(s)
          .filter((e) => e.screen === 'people')
          .map((e) => navRow(s, e, 'margin-top:10px'))
      : []),

    here.id === 'home'
      ? h(
          'button',
          {
            type: 'button',
            class: 'navitem',
            style: canManagePeople(s) ? undefined : 'margin-top:10px',
            title: 'Settings — this device, your role, and signing out',
            onclick: () => actions.openApp('inspection', 'options'),
          },
          h('span', { class: 'navitem__icon' }, icon(ICONS.settings, { size: 17 })),
          s.navOpen ? h('span', { class: 'navitem__text', text: t('Settings') }) : null,
        )
      : null,

    h(
      'button',
      {
        type: 'button',
        class: 'navitem',
        style: here.id === 'home' ? undefined : 'margin-top:10px',
        title: s.navOpen ? 'Collapse navigation (B)' : 'Expand navigation (B)',
        'aria-label': s.navOpen ? 'Collapse navigation' : 'Expand navigation',
        onclick: () => actions.toggleNav(),
      },
      h('span', { class: 'navitem__icon' }, icon(ICONS.menu, { size: 17 })),
      s.navOpen ? h('span', { class: 'navitem__text', text: 'Collapse' }) : null,
      s.navOpen ? h('span', { class: 'navitem__key', text: 'B' }) : null,
    ),
  );
}
